import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMusic } from '@fortawesome/pro-light-svg-icons/faMusic';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { CatalogSong } from '../../../../../../redux/api/fymAPI.types';
import { TypedMemo } from '../../../../../common/types';
import { catalogSongMessages } from '../../catalogSongMessages';
import { formatSongLength } from '../../masterFile';
import { MasterUploadRow } from '../../MasterUploadRow';
import { formatCatalogDay } from '../../../catalogDates';
import { songPageMessages } from '../songPageMessages';
import { Panel, PanelBody, PanelHead } from '../../../../../music24/components/primitives/Panel';
import { withBrandWord } from '../../../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from '../SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongFilesSectionProps {
  accountId: number;
  itemUuid: string;
  song: CatalogSong;
  /** The hand-set times of the lyric timing: Replace says that they go (c6). */
  editedCount: number;
  readOnly: boolean;
  isUploading: boolean;
  onUploadingChange: (isUploading: boolean) => void;
}

/**
 * Song files (design c1 to c6): the master in each state (none, picked, uploading, reading, ready,
 * failed, replace), the facts of the analysis, and what Music24 keeps from the master. Every
 * member uploads, plays and downloads (D7). The upload starts at once (owner decision 1).
 */
const SongFilesSectionComponent: React.FC<SongFilesSectionProps> = ({
  accountId,
  itemUuid,
  song,
  editedCount,
  readOnly,
  isUploading,
  onUploadingChange,
}) => {
  const intl = useIntl();
  const { master } = song;
  const empty = '—';
  const facts = useMemo(
    () => [
      {
        key: 'length',
        label: intl.formatMessage(songPageMessages.factLength),
        value: master === null || master.durationSec === null ? null : formatSongLength(master.durationSec),
      },
      {
        key: 'tempo',
        label: intl.formatMessage(songPageMessages.factTempo),
        value:
          master === null || master.bpm === null
            ? null
            : intl.formatMessage(catalogSongMessages.bpm, { bpm: Math.round(master.bpm) }),
      },
      {
        key: 'hook',
        label: intl.formatMessage(songPageMessages.factHook),
        value: master === null || master.hookStartSec === null ? null : formatSongLength(master.hookStartSec),
      },
      {
        key: 'beat',
        label: intl.formatMessage(songPageMessages.factBeat),
        value:
          master === null || master.beatGrid === undefined || master.beatGrid === null
            ? null
            : intl.formatMessage(
                master.beatGrid === 'detected' ? songPageMessages.beatDetected : songPageMessages.beatFallback
              ),
      },
    ],
    [intl, master]
  );
  const kept = useMemo(() => {
    const parts = song.kept ?? { playCopy: false, vocalStem: false, analysis: false };
    return [
      parts.playCopy && {
        key: 'play',
        title: intl.formatMessage(songPageMessages.keptPlayCopy),
        note: intl.formatMessage(songPageMessages.keptPlayCopyNote),
      },
      parts.vocalStem && {
        key: 'stem',
        title: intl.formatMessage(songPageMessages.keptVocalStem),
        note: intl.formatMessage(songPageMessages.keptVocalStemNote),
      },
      parts.analysis && {
        key: 'analysis',
        title: intl.formatMessage(songPageMessages.keptAnalysis),
        note: intl.formatMessage(songPageMessages.keptAnalysisNote),
      },
    ].filter((row): row is { key: string; title: string; note: string } => row !== false);
  }, [intl, song.kept]);

  return (
    <div className={styles.cols}>
      <Panel>
        <PanelHead
          title={intl.formatMessage(songPageMessages.masterTitle)}
          right={
            <span className={styles.meta}>
              {master !== null && master.uploadedAt !== undefined && master.uploadedAt !== null
                ? intl.formatMessage(songPageMessages.masterUploaded, {
                    date: formatCatalogDay(intl, master.uploadedAt),
                  })
                : intl.formatMessage(songPageMessages.masterUsedBy)}
            </span>
          }
        />
        <PanelBody className={styles.cardBody}>
          <MasterUploadRow
            accountId={accountId}
            itemUuid={itemUuid}
            master={master}
            showPlayer={false}
            readOnly={readOnly}
            replaceNote={
              editedCount > 0 ? intl.formatMessage(songPageMessages.replaceEdits, { count: editedCount }) : undefined
            }
            onUploadingChange={onUploadingChange}
          />
          {isUploading && (
            <div className={clsx(styles.note, styles.noteWarn)}>
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>{intl.formatMessage(songPageMessages.uploadKeepOpen)}</span>
            </div>
          )}
          {master !== null && master.status !== 'failed' && (
            <>
              <div className={styles.facts} data-testid="song-page-facts">
                {facts.map((fact) => (
                  <div key={fact.key} className={styles.fact}>
                    <span>{fact.label}</span>
                    <b className={clsx(fact.value === null && styles.factEmpty)}>{fact.value ?? empty}</b>
                  </div>
                ))}
              </div>
              <span className={styles.help}>{withBrandWord(intl.formatMessage(songPageMessages.hookHelp))}</span>
            </>
          )}
        </PanelBody>
      </Panel>
      <Panel>
        <PanelHead title={intl.formatMessage(songPageMessages.keptTitle)} />
        <PanelBody className={styles.cardBody}>
          {kept.length === 0 ? (
            <span className={styles.help}>{withBrandWord(intl.formatMessage(songPageMessages.keptNothing))}</span>
          ) : (
            <div>
              {kept.map((row) => (
                <div key={row.key} className={styles.keptRow}>
                  <FontAwesomeIcon icon={faMusic} />
                  <div className={styles.grow}>
                    <b>{row.title}</b>
                    <small>{row.note}</small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </PanelBody>
      </Panel>
    </div>
  );
};

export const SongFilesSection = TypedMemo(SongFilesSectionComponent);
